import { createFileRoute } from "@tanstack/react-router";
import { ogImageMeta } from "@/lib/seo/og-image";
import { LocaleLink } from "@/components/site/LocaleLink";
import {
  ArrowRight,
  Languages,
  UserCheck,
  ShieldCheck,
  Gauge,
  Zap,
  Eye,
  Workflow,
  Layers,
  Compass,
} from "lucide-react";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { MethodologyTeaser } from "@/components/site/MethodologyTeaser";
import { Reveal, RevealItem } from "@/components/site/Reveal";
import { Button } from "@/components/ui/button";

import { pageSchemas } from "@/lib/seo/page-schema";
export const Route = createFileRoute("/why-perceptica")({
  head: () => ({
    meta: [
      { title: "Why Perceptica - Media Intelligence Built for SEE" },
      { name: "description", content: "Why teams pick Perceptica for media monitoring in Southeast Europe - analyst-grade media intelligence, full SEE coverage and transparent methodology." },
      { property: "og:title", content: "Why Perceptica - Media Intelligence Built for SEE" },
      { property: "og:description", content: "Why teams pick Perceptica for media monitoring in Southeast Europe - analyst-grade media intelligence, full SEE coverage and transparent methodology." },

      ...ogImageMeta("en", "/why-perceptica", "Why Perceptica"),
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Why Perceptica - Media Intelligence Built for SEE" },
      { name: "twitter:description", content: "Why teams pick Perceptica for media monitoring in Southeast Europe - analyst-grade media intelligence, full SEE coverage and transparent methodology." },
      { property: "og:url", content: "https://perceptica.com/why-perceptica" },
      { property: "og:type", content: "website" },
    ],
  
    scripts: pageSchemas({ locale: "en", path: "/why-perceptica" }),
  }),
  component: WhyPerceptica,
});

function WhyPerceptica() {
  const pillars = [
    {
      icon: Languages,
      title: "Native, not translated",
      body: "Sentiment scored in-language across 25+ SEE and EU markets. No English pivot, no lost nuance.",
    },
    {
      icon: UserCheck,
      title: "Analyst-in-the-loop",
      body: "Senior analysts in Sofia validate edge cases daily and recalibrate models every week.",
    },
    {
      icon: ShieldCheck,
      title: "There when it matters",
      body: "High-agency analysts who pick up when a story breaks - including the calls that don't fit a 9-to-5 calendar.",
    },
    {
      icon: Gauge,
      title: "Crisis-grade speed",
      body: "Early-warning alerts within minutes, with the human context PR teams actually act on.",
    },
  ];

  const reasons = [
    {
      icon: Eye,
      title: "You see what your audience sees",
      body: "Tier-1, regional and policy outlets, broadcast, social and the long tail - covered in the languages your stakeholders read.",
    },
    {
      icon: Zap,
      title: "You get judgment, not just dashboards",
      body: "Every account works with a senior analyst. Briefings, narrative reads and crisis playbooks come included.",
    },
    {
      icon: Workflow,
      title: "You ship reports faster",
      body: "Templates, custom topics and ready-made AMEC outputs cut weekly reporting from days to hours.",
    },
    {
      icon: Layers,
      title: "You stay compliant by default",
      body: "EU data residency, transparent pricing and contracts built for regulated industries.",
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main id="main">
        {/* HERO - dark editorial */}
        <section className="relative overflow-hidden bg-primary text-primary-foreground">
          <div className="grid-overlay" />
          <div
            aria-hidden
            className="pointer-events-none absolute -top-32 right-0 h-[520px] w-[520px] rounded-full opacity-30 blur-[160px]"
            style={{ background: "radial-gradient(circle, #FF2D78 0%, transparent 70%)" }}
          />
          <div className="noise-overlay" />
          <div className="relative mx-auto max-w-[1100px] px-6 py-24 lg:py-32">
            <Reveal className="max-w-3xl">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-accent">
                Why Perceptica
              </p>
              <h1 className="font-display mt-5 text-[clamp(2.5rem,5vw,4rem)] font-medium leading-[1.04] tracking-[-0.02em] text-white">
                Media intelligence built for the markets you actually operate in.
              </h1>
              <p className="mt-6 max-w-xl text-[15.5px] leading-relaxed text-white/70">
                Native-language sentiment, senior analysts on every account, and reporting that holds up in the boardroom - without the translation tax of global platforms.
              </p>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Button asChild size="lg">
                  <LocaleLink to="/request-a-demo">
                    Request a demo
                    <ArrowRight className="h-4 w-4" />
                  </LocaleLink>
                </Button>
              </div>
            </Reveal>
          </div>
        </section>

        {/* DIFFERENTIATOR PILLARS */}
        <section className="py-20 lg:py-28">
          <div className="mx-auto max-w-[1200px] px-6 lg:px-10">
            <Reveal className="max-w-2xl">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-accent">
                What sets us apart
              </p>
              <h2 className="font-display mt-3 text-[clamp(1.75rem,2.8vw,2.5rem)] text-foreground">
                Four reasons teams pick Perceptica over global platforms.
              </h2>
            </Reveal>
            <Reveal stagger className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {pillars.map((p) => (
                <RevealItem key={p.title}>
                  <div className="h-full rounded-2xl border border-border/60 bg-background p-6 shadow-[var(--shadow-soft)] transition-colors hover:border-accent/50">
                    <span className="inline-flex h-11 w-11 items-center justify-center rounded-[10px] bg-gradient-brand text-white">
                      <p.icon className="h-5 w-5" />
                    </span>
                    <h3 className="font-display mt-5 text-[17px] leading-snug text-foreground">
                      {p.title}
                    </h3>
                    <p className="mt-2 text-[13.5px] leading-relaxed text-muted-foreground">
                      {p.body}
                    </p>
                  </div>
                </RevealItem>
              ))}
            </Reveal>
          </div>
        </section>

        {/* METHODOLOGY - the strategic wedge */}
        <MethodologyTeaser />

        {/* WHY TEAMS SWITCH */}
        <section className="py-20 lg:py-28">
          <div className="mx-auto max-w-[1200px] px-6 lg:px-10">
            <div className="grid gap-12 lg:grid-cols-[1fr_1.4fr] lg:gap-20">
              <Reveal>
                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-accent">
                  Why teams switch
                </p>
                <h2 className="font-display mt-3 text-[clamp(1.75rem,2.8vw,2.5rem)] text-foreground">
                  The shift from generic listening to decision-grade intelligence.
                </h2>
                <p className="mt-5 text-[14.5px] leading-relaxed text-muted-foreground">
                  Customers come to us when dashboards stop answering the questions their leadership is actually asking - about reputation, narrative momentum and competitive share of voice in markets global tools treat as edge cases.
                </p>
              </Reveal>
              <Reveal stagger className="grid gap-px overflow-hidden rounded-2xl border border-border/60 bg-border/60 sm:grid-cols-2">
                {reasons.map((r) => (
                  <RevealItem key={r.title}>
                    <div className="h-full bg-background p-7">
                      <span className="inline-flex h-10 w-10 items-center justify-center rounded-[10px] bg-accent/10 text-accent">
                        <r.icon className="h-5 w-5" />
                      </span>
                      <h3 className="font-display mt-5 text-[17px] leading-snug text-foreground">
                        {r.title}
                      </h3>
                      <p className="mt-2 text-[13.5px] leading-relaxed text-muted-foreground">
                        {r.body}
                      </p>
                    </div>
                  </RevealItem>
                ))}
              </Reveal>
            </div>
          </div>
        </section>

        {/* COVERAGE TEASER - LIGHT */}
        <section className="bg-secondary/40 py-20 lg:py-24">
          <div className="mx-auto max-w-[1100px] px-6 lg:px-10">
            <div className="grid items-center gap-10 lg:grid-cols-[1.1fr_1fr]">
              <Reveal>
                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-accent">Coverage</p>
                <h2 className="font-display mt-3 text-[clamp(1.75rem,2.6vw,2.25rem)] text-foreground">
                  Coverage that actually covers your market.
                </h2>
                <p className="mt-4 max-w-md text-[14.5px] leading-relaxed text-muted-foreground">
                  Press, broadcast, online news, blogs and the full social conversation - across the languages and markets that matter to you.
                </p>
                <LocaleLink
                  to="/media-monitoring/coverage"
                  className="mt-6 inline-flex items-center gap-2 text-[13px] font-semibold tracking-[-0.005em] text-accent hover:text-accent/80"
                >
                  See full coverage
                  <ArrowRight className="h-3.5 w-3.5" />
                </LocaleLink>
              </Reveal>
              <Reveal stagger className="grid grid-cols-3 gap-4">
                {[
                  { value: "25+", label: "Languages" },
                  { value: "60k+", label: "Sources monitored" },
                  { value: "SE Europe", label: "+ global footprint" },
                ].map((stat) => (
                  <RevealItem key={stat.label}>
                    <div className="rounded-2xl border border-border/60 bg-background p-5">
                      <p className="font-display text-[clamp(1.5rem,2.4vw,2rem)] tracking-[-0.02em] text-foreground">
                        {stat.value}
                      </p>
                      <p className="mt-2 text-[12px] leading-snug text-muted-foreground">{stat.label}</p>
                    </div>
                  </RevealItem>
                ))}
              </Reveal>
            </div>
          </div>
        </section>

        {/* FINAL CTA - rounded dark card */}
        <section className="bg-background py-20 lg:py-28">
          <div className="mx-auto max-w-[1200px] px-6 lg:px-10">
            <Reveal>
              <div className="relative overflow-hidden rounded-3xl bg-primary px-8 py-14 text-primary-foreground sm:px-12 lg:px-16 lg:py-20">
                {/* grid texture */}
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-0 opacity-[0.08]"
                  style={{
                    backgroundImage:
                      "linear-gradient(to right, currentColor 1px, transparent 1px), linear-gradient(to bottom, currentColor 1px, transparent 1px)",
                    backgroundSize: "56px 56px",
                    color: "var(--background)",
                  }}
                />
                {/* glow */}
                <div
                  aria-hidden
                  className="pointer-events-none absolute -bottom-32 -right-32 h-[420px] w-[420px] rounded-full opacity-50 blur-[140px]"
                  style={{ background: "radial-gradient(circle, #FF2D78 0%, transparent 70%)" }}
                />

                <div className="relative grid gap-10 lg:grid-cols-[1.4fr_1fr] lg:items-center">
                  <div>
                    <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/80">
                      <Compass className="h-3.5 w-3.5 text-accent" />
                      Tailored to your market
                    </span>
                    <h2 className="font-display mt-6 max-w-[18ch] text-[clamp(1.85rem,3.4vw,2.75rem)] font-medium leading-[1.08] tracking-[-0.01em] text-white">
                      Ready to see Perceptica with your own brand data?
                    </h2>
                    <p className="mt-5 max-w-md text-[14.5px] leading-relaxed text-white/65">
                      Talk to our team about a media intelligence programme tailored to your industry, languages and objectives.
                    </p>
                  </div>

                  <div className="flex flex-col items-start gap-4 lg:items-end">
                    <Button
                      asChild
                      size="xl"
                      className="bg-accent text-accent-foreground shadow-[0_18px_40px_-18px_color-mix(in_oklab,var(--accent)_70%,transparent)] hover:bg-accent/90"
                    >
                      <LocaleLink to="/request-a-demo">
                        Request a demo
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </LocaleLink>
                    </Button>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/80">
                      Most requests receive a reply from us within one business day.
                    </p>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
